import { InstallerHint } from '../components/BulkBar';
import { ago, hostView, plural, pct, SERVERS, svgPath, whenLabel } from '../format';
import { useApp, useTheme } from '../store';
import { btn, divider, ellipsis, head, mono, panel, panelPad, sectionTitle } from '../ui';

export function Dashboard() {
  const app = useApp();
  const theme = useTheme();

  const total = app.hosts.length;
  const online = app.hosts.filter(h => h.state !== 'offline');
  const warnList = app.hosts.filter(h => h.state === 'warn');

  const kpis = [
    { label: 'Всего серверов', value: total, unit: 'хостов', note: `${new Set(app.hosts.map(h => h.loc)).size} локации · ${new Set(app.hosts.map(h => h.os)).size} версии ОС`, color: 'var(--ink)' },
    { label: 'На связи', value: online.length, unit: 'агентов', note: `heartbeat меньше ${app.runtime.offlineAfterSec} с`, color: 'var(--ok)' },
    { label: 'Требуют внимания', value: warnList.length, unit: 'хостов', note: 'CPU выше 88% или перегрев', color: 'var(--warn)' },
    { label: 'Выбрано сейчас', value: app.selCount, unit: 'хостов', note: 'действия — в панели снизу', color: app.selCount ? 'var(--acc)' : 'var(--ink3)' }
  ];

  const avg = app.series.avg.length ? app.series.avg : new Array(24).fill(0);
  const peak = app.series.peak.length ? app.series.peak : new Array(24).fill(0);
  const medianCpu = median(avg);

  const withTemp = app.hosts.filter(h => h.temp > 0);
  const bands = [
    { label: '< 55°', color: theme.heat[1], count: withTemp.filter(h => h.temp < 55).length },
    { label: '55–69°', color: theme.heat[2], count: withTemp.filter(h => h.temp >= 55 && h.temp < 70).length },
    { label: '70–82°', color: 'var(--warn)', count: withTemp.filter(h => h.temp >= 70 && h.temp <= 82).length },
    { label: '> 82°', color: 'var(--bad)', count: withTemp.filter(h => h.temp > 82).length }
  ];

  const hottest = [...app.hosts].sort((a, b) => b.temp - a.temp).slice(0, 5);

  const heatLegend = [
    { color: theme.heat[1], label: '<30' },
    { color: theme.heat[2], label: '30–69' },
    { color: theme.heat[3], label: '70–89' },
    { color: theme.heat[4], label: '90+' },
    { color: theme.heat[0], label: 'офлайн' }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pad)' }}>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(212px,1fr))', gap: 14 }}>
        {kpis.map(k => (
          <div
            key={k.label}
            style={{ ...panel, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 9 }}
          >
            <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{k.label}</span>
            <span style={{ display: 'flex', alignItems: 'flex-end', gap: 9 }}>
              <span
                style={{
                  ...head,
                  fontSize: 34,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: '-0.03em',
                  color: k.color
                }}
              >
                {k.value}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--ink3)', paddingBottom: 3 }}>{k.unit}</span>
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--ink2)' }}>{k.note}</span>
          </div>
        ))}
      </div>

      {/* График загрузки + температура */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
        <section style={panelPad}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 14,
              marginBottom: 16
            }}
          >
            <span style={sectionTitle}>Загрузка CPU по парку</span>
            <span style={{ ...mono, fontSize: 11.5, color: 'var(--ink3)' }}>
              24 ч · медиана {medianCpu}%
            </span>
          </div>
          <svg
            viewBox="0 0 720 200"
            preserveAspectRatio="none"
            style={{ width: '100%', height: 200, display: 'block', overflow: 'visible' }}
          >
            <g stroke="var(--grid)" strokeWidth="1">
              <line x1="0" y1="50" x2="720" y2="50" />
              <line x1="0" y1="100" x2="720" y2="100" />
              <line x1="0" y1="150" x2="720" y2="150" />
            </g>
            <path d={svgPath(avg, 720, 200, 100) + ' L720 200 L0 200 Z'} fill="var(--acc)" fillOpacity="0.14" />
            <path d={svgPath(peak, 720, 200, 100)} fill="none" stroke="var(--ink3)" strokeWidth="1.25" strokeDasharray="4 4" />
            <path d={svgPath(avg, 720, 200, 100)} fill="none" stroke="var(--acc)" strokeWidth="2.25" strokeLinejoin="round" />
          </svg>
          <div style={{ display: 'flex', gap: 20, marginTop: 12, fontSize: 11.5, color: 'var(--ink3)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 16, height: 2, background: 'var(--acc)' }} />
              средняя
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 16, height: 0, borderTop: '1px dashed var(--ink3)' }} />
              пик по парку
            </span>
          </div>
        </section>

        <section style={{ ...panelPad, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={sectionTitle}>Температура CPU</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {bands.map(b => (
              <div
                key={b.label}
                style={{ display: 'grid', gridTemplateColumns: '60px 1fr 30px', alignItems: 'center', gap: 11 }}
              >
                <span style={{ ...mono, fontSize: 11.5, color: 'var(--ink2)' }}>{b.label}</span>
                <span style={{ display: 'block', height: 8, borderRadius: 99, background: 'var(--track)' }}>
                  <span
                    style={{
                      display: 'block',
                      height: 8,
                      borderRadius: 99,
                      background: b.color,
                      width: pct(b.count, total)
                    }}
                  />
                </span>
                <span style={{ ...mono, fontSize: 11.5, textAlign: 'right' }}>{b.count}</span>
              </div>
            ))}
          </div>
          <div style={divider} />
          <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Самые горячие</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hottest.map(h => {
              const v = hostView(h, theme, !!app.sel[h.id]);
              return (
                <button
                  key={h.id}
                  className="hov"
                  onClick={() => app.openHost(h.id)}
                  style={{
                    ...btn,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    gap: 12,
                    alignItems: 'baseline',
                    padding: '6px 8px',
                    margin: '0 -8px',
                    borderRadius: 'var(--rs)',
                    ...mono,
                    fontSize: 11.5
                  }}
                >
                  <span>{h.name}</span>
                  <span style={{ color: v.tempColor, fontWeight: 500 }}>{v.tempStr}</span>
                  <span style={{ color: 'var(--ink3)' }}>{v.cpuStr}</span>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {/* Тепловая карта парка */}
      <section style={panelPad}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
          <span style={sectionTitle}>Карта парка</span>
          <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>клик добавляет хост в массовый выбор</span>
          <span style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>CPU</span>
            {heatLegend.map(l => (
              <span
                key={l.label}
                style={{ display: 'flex', alignItems: 'center', gap: 5, ...mono, fontSize: 10.5, color: 'var(--ink3)' }}
              >
                <span style={{ width: 11, height: 11, borderRadius: 3, background: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {app.hosts.map(h => {
            const v = hostView(h, theme, !!app.sel[h.id]);
            return (
              <button
                key={h.id}
                className="lift"
                title={v.tip}
                onClick={() => app.toggleHost(h.id)}
                style={{
                  ...btn,
                  position: 'relative',
                  width: 38,
                  height: 38,
                  borderRadius: 'var(--rs)',
                  boxSizing: 'border-box',
                  background: v.heatBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...mono,
                  fontSize: 9.5,
                  color: v.heatFg
                }}
              >
                {v.selected && (
                  <span
                    style={{
                      position: 'absolute',
                      inset: -3,
                      borderRadius: 'var(--r)',
                      border: '2px solid var(--acc)'
                    }}
                  />
                )}
                {h.short}
              </button>
            );
          })}
          {!app.hosts.length && (
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>
              Парк пуст. Установите агент хотя бы на один сервер — или запустите сервер с DEMO=1.
            </span>
          )}
        </div>

        {!app.hosts.some(h => !h.simulated) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>
              Живых агентов пока нет — поставьте агент на сервер, и он появится здесь.
            </span>
            <InstallerHint />
          </div>
        )}
      </section>

      {/* Инциденты + последние задачи */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 14 }}>
        <section style={{ ...panelPad, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={sectionTitle}>Инциденты</span>
          {app.alerts.map(a => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                padding: 12,
                borderRadius: 'var(--rs)',
                background: 'var(--panel2)'
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  marginTop: 5,
                  flex: 'none',
                  background: sevColor(a.sev)
                }}
              />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{a.title}</span>
                <span style={{ ...mono, fontSize: 11, color: 'var(--ink3)' }}>{a.meta}</span>
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink3)', flex: 'none' }}>{ago(a.since)}</span>
            </div>
          ))}
          {!app.alerts.length && (
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Открытых инцидентов нет.</span>
          )}
        </section>

        <section style={{ ...panelPad, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={sectionTitle}>Последние массовые задачи</span>
          {app.jobs.slice(0, 4).map(j => (
            <button
              key={j.id}
              className="hov"
              onClick={() => app.go('tasks')}
              style={{
                ...btn,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '10px 12px',
                margin: '0 -12px',
                borderRadius: 'var(--rs)'
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%' }}>
                <span style={{ ...mono, fontSize: 11.5, ...ellipsis }}>{j.cmd}</span>
                <span style={{ fontSize: 11, color: 'var(--ink3)', flex: 'none' }}>{whenLabel(j.createdAt)}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <span style={{ flex: 1, display: 'block', height: 5, borderRadius: 99, background: 'var(--track)' }}>
                  <span
                    style={{
                      display: 'block',
                      height: 5,
                      borderRadius: 99,
                      background: j.err ? 'var(--warn)' : 'var(--ok)',
                      width: pct(j.ok, j.total)
                    }}
                  />
                </span>
                <span style={{ ...mono, fontSize: 11, color: 'var(--ink2)' }}>
                  {j.ok}/{j.total}
                </span>
              </span>
            </button>
          ))}
          {!app.jobs.length && (
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>
              Массовых задач ещё не было. Выберите хосты и запустите команду.
            </span>
          )}
        </section>
      </div>

      <span style={{ fontSize: 11, color: 'var(--ink4)' }}>
        {plural(app.hosts.length, SERVERS)} в парке · срез обновляется каждые 2 секунды
      </span>
    </div>
  );
}

function sevColor(sev: string): string {
  if (sev === 'bad') return 'var(--bad)';
  if (sev === 'warn') return 'var(--warn)';
  return 'var(--ink4)';
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}
